import React, { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import {
  Button,
  Card,
  Divider,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  Icon,
  Label,
  Loader,
  Menu,
  Message,
  Popup,
} from "semantic-ui-react";
import { styled, useTheme } from "styled-components";

import { initUseQuery } from "simplydo/core/query";
import SearchParams from "simplydo/core/searchParams";
import type { OpenAPI } from "simplydo/interfaces";
import { Input } from "simplydo/react/component-library";
import { LabelChooser } from "simplydo/react/components/Choosers/LabelChooser/LabelChooser";
import { useThrottle } from "simplydo/react/hooks";

import actions from "actions";
import api from "api";
import {
  type ChallengesReducer,
  defaultInitialState as challengeReducerDefaultState,
} from "reducers/challenges";
import { useAppDispatch, useAppSelector } from "store";
import { useDidMount } from "utils/useDidMount";
import util from "utils/utils";

import { ChallengeChooser, UserChooser } from "components/lib/Choosers";
import { AssessorAudienceWarning } from "components/lib/Choosers/UserChooser/Content/ChallengeAudienceWarning";
import ConfirmAnnouncementModal from "components/lib/ConfirmAnnouncementModal";
import ChallengeIdeaFilters, {
  ChallengeFilterKeys,
} from "components/lib/Filters/ChallengeIdeaFilters";
import { GatedChallengeModal } from "components/lib/GatedChallengeModal";
import { GroupUserManagementModal } from "components/lib/GroupUserManagementModal";
import { confirmMerge } from "components/lib/IdeaMerge";
import IdeaPdfExport from "components/lib/IdeaPdfExport";
import { HandledIdeaPreview as IdeaPreview } from "components/lib/Ideas";
import PinIdeaHelper from "components/lib/Ideas/PinIdeaHelper";
import ProductTour from "components/lib/ProductTour";
import ProjectBoard from "components/lib/ProjectBoard";
import BoardEvents from "components/lib/ProjectBoard/BoardEvents";
import { EmptyBox } from "components/lib/UI";

import {
  IdeaDeadlineModal,
  IdeaDeletionModal,
  IdeaManagementDeadlineModal,
  IdeaMoveToLaneModal,
  Pagination,
} from "./IdeaActions";
import IdeaCard from "./IdeaCard";
import ChallengeMyIdeas from "./MyIdeas";
import { ActionPopup, IdeaListContainer } from "./Styles";

const StyledIdeasContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

const StyledIdeaItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 100%;
  flex: 1;
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: ${({ theme }) =>
    theme.sizes.isMobile ? "column-reverse" : "row"};
  align-items: ${({ theme }) =>
    !theme.sizes.isMobile ? "flex-start" : "center"};
  justify-content: space-between;
  margin-bottom: 5px;
`;

const StyledFilters = styled.div`
  display: flex;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? "column" : "row")};
  align-items: center;
  gap: 10px;
  .ui.button,
  a,
  .ui.dropdown {
    min-width: fit-content;
    ${({ theme }) =>
      theme.sizes.isMobile &&
      `
      margin-bottom: 5px;
      width: 100%;
    `}
  }
  .ui.button,
  .ui.dropdown {
    height: 33px;
  }
  ${({ theme }) =>
    theme.sizes.isMobile &&
    `
    width: 100%;
  `}
  ${({ theme }) =>
    !theme.sizes.isLargeComputer &&
    `
    margin-bottom: 5px;
  `}
`;

const IdeaViewTypes = [
  { key: "card", text: "Card", value: "card", icon: "vcard" },
  { key: "list", text: "List", value: "list", icon: "list" },
  { key: "board", text: "Board", value: "board", icon: "trello" },
];

export const useIdeaQueryParams = (ideaViewType: string) => {
  const challengesReducer: ChallengesReducer = useAppSelector(
    (state) => state.challenges,
  );
  const {
    tagFilter,
    ideaFilter: query,
    ideaIncludes: filter,
    ideaOrder: sort,
    ideaOrderDirection: sortDirection,
    ideaPage: page,
    labelsFilter: labels,
    ideaLimit: limit,
    ideaFieldChoices: fieldChoices,
    projectBoardLanesFilter,
    groupFilter,
    orgFilter,
    projectBoardFilter,
    adminHasCommented,
    impactFilter,
  } = challengesReducer;

  return useMemo(
    () => ({
      page: ideaViewType === "board" ? 1 : page,
      query,
      tags: tagFilter,
      filter: filter || "all",
      sort,
      sortDirection,
      labels,
      groupFilter,
      orgFilter,
      fieldChoices,
      limit: ideaViewType === "board" ? 10000 : limit,
      adminHasCommented,
      projectBoardFilter,
      projectBoardLanesFilter,
      impactFilter,
    }),
    [
      ideaViewType,
      page,
      query,
      tagFilter,
      filter,
      sort,
      sortDirection,
      labels,
      groupFilter,
      orgFilter,
      fieldChoices,
      limit,
      adminHasCommented,
      projectBoardFilter,
      projectBoardLanesFilter,
      impactFilter,
    ],
  );
};

type ChallengeIdea =
  OpenAPI.GET<"/challenges/{id}/ideas">["response"]["ideas"][number];

type IdeasProps = {
  challenge: OpenAPI.GET<"/challenges/{id}">["response"];
  newIdea: () => void;
};

const Ideas = ({ challenge, newIdea }: IdeasProps) => {
  const [actionPopupOpen, setActionPopupOpen] = useState(false);

  const dispatch = useAppDispatch();

  const onFollow = useCallback(
    (context: string, id: string) => {
      dispatch(actions.user.onFollow(context, id));
    },
    [dispatch],
  );

  const updateFilters = useCallback(
    (filters: Partial<ChallengesReducer>) => {
      dispatch(actions.challenges.updateIdeaFilters(filters));
    },
    [dispatch],
  );

  const setIdeaViewType = useCallback(
    (viewType: string, resetParams = true) => {
      if (resetParams) {
        // Remove view type from query params
        const params = new SearchParams(window.location.search);
        params.delete("queryView");
        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}?${params}`,
        );
      }
      if (
        ["card", "list"].includes(viewType) &&
        util.localStorageIsSupported()
      ) {
        localStorage.setItem("ideaViewTypePreference", viewType);
      }

      dispatch(actions.challenges.setIdeaViewType(viewType));
    },
    [dispatch],
  );

  const { t } = useTranslation();
  const navigate = useNavigate();

  const user: OpenAPI.GET<"/users/me">["response"] | undefined = useAppSelector(
    (state) => state.user,
  );
  const hasUser: boolean = !!user;
  const { ownerOrganisation } = user ?? {};
  const ideaMergeEnabled =
    ownerOrganisation?.enabledFeatures?.includes("ideaMerging");
  const canMakeIdea = util.canCreateIdea(user, challenge);
  const coverImageEnabled = !challenge.preventIdeaCoverImages;

  const challengeId = challenge._id!;
  const shouldFetchIdeas = useMemo(
    () => !!challengeId && hasUser,
    [challengeId, hasUser],
  );

  const canManageChallenge = useMemo(
    () => util.canManageChallenge(user, challenge),
    [user, challenge],
  );
  const canActionIdeas = useMemo(
    () => util.canActionIdeas(user, challenge),
    [user, challenge],
  );
  const canViewProjectBoard = useMemo(
    () =>
      util.hasPermission(user, "challenge.viewProjectBoard", challenge._id) ||
      challenge.projectManagementVisibility === "users",
    [user, challenge],
  );

  const challengesReducer: ChallengesReducer = useAppSelector(
    (state) => state.challenges,
  );
  const {
    ideaIncludes: filter,
    ideaPage: page,
    ideaLimit: limit,
    projectBoardLanesFilter,
    ideaViewType,
    filtersVisible,
    postFetchPreviewIndex,
  } = challengesReducer;

  const queryParams = useIdeaQueryParams(ideaViewType);
  const basicIdeaQuery = useQuery(
    initUseQuery(
      api.challenges.getIdeas,
      [challengeId!, { ...queryParams, contentType: "basic" }],
      { isEnabled: shouldFetchIdeas },
    ),
  );

  const fullIdeaQuery = useQuery(
    initUseQuery(
      api.challenges.getIdeas,
      [challengeId!, { ...queryParams, contentType: "full" }],
      { isEnabled: shouldFetchIdeas },
    ),
  );

  const ideaFilterQuery = useQuery(
    initUseQuery(api.challenges.getIdeaFilters, [challengeId!], {
      isEnabled: shouldFetchIdeas,
    }),
  );

  const projectLaneQuery = useQuery(
    initUseQuery(api.boards.getProjectLanes, [challengeId!], {
      isEnabled: canManageChallenge || canViewProjectBoard,
    }),
  );

  const data = useMemo(() => {
    return (
      fullIdeaQuery.data ||
      basicIdeaQuery.data ||
      ({
        ideas: [],
        nextPageAvailable: false,
        previousPageAvailable: false,
        total: 0,
        laneImpactMeasures: {},
        page: 1,
        ideasVotedFor: 0,
        invitations: [],
      } as OpenAPI.GET<"/challenges/{id}/ideas">["response"])
    );
  }, [fullIdeaQuery.data, basicIdeaQuery.data]);

  const [showMyIdeas, setShowMyIdeas] = useState(true);

  const [challengeChooserIdea, setChallengeChooserIdea] = useState("");
  const [_selectedIdeas, setSelectedIdeas] = useState<ChallengeIdea[]>([]);
  const [assessmentAssignIdeas, setAssessmentAssignIdeas] = useState<
    ChallengeIdea[]
  >([]);
  const [assessmentAssignRoundId, setAssessmentAssignRoundId] = useState("");
  const [nextPhaseChallenge, setNextPhaseChallenge] = useState([]);
  const [deletingIdeas, setDeletingIdeas] = useState<ChallengeIdea[]>([]);
  const [labelIdeas, setLabelIdeas] = useState<ChallengeIdea[]>([]);
  const [projectAssignIdeas, setProjectAssignIdeas] = useState<ChallengeIdea[]>(
    [],
  );
  const [deadlineIdeas, setDeadlineIdeas] = useState<ChallengeIdea[]>([]);
  const [managementDeadlineIdeas, setManagementDeadlineIdeas] = useState<
    ChallengeIdea[]
  >([]);
  const [moveLaneIdeas, setMoveLaneIdeas] = useState<ChallengeIdea[]>([]);
  const [groupAddIdeas, setGroupAddIdeas] = useState<ChallengeIdea[]>([]);
  const [previewId, setPreviewId] = useState("");
  const [removingIdeaFromMerge, setRemovingIdeaFromMerge] = useState("");
  const [confirmAnnouncementModalOpen, setConfirmAnnouncementModalOpen] =
    useState(false);
  const [laneAnnouncements, setLaneAnnouncements] = useState<
    Array<OpenAPI.Schemas["Announcement"]>
  >([]);
  const [laneUpdateContext, setLaneUpdateContext] = useState<{
    ideaIds?: string[];
    ideaId?: string;
    laneId?: string;
  }>({});

  useEffect(() => {
    if (postFetchPreviewIndex === null || postFetchPreviewIndex === undefined)
      return;
    if (fullIdeaQuery.isFetching) return;
    dispatch(
      actions.challenges.updateIdeaFilters({ postFetchPreviewIndex: null }),
    );
    const ideas = fullIdeaQuery.data?.ideas || [];
    const ideaAtIndex = ideas[postFetchPreviewIndex];
    if (ideaAtIndex) {
      setPreviewId(ideaAtIndex._id!);
    }
  }, [dispatch, fullIdeaQuery, postFetchPreviewIndex]);

  const ideaMap = useMemo(() => {
    const map: { [key: string]: ChallengeIdea } = {};
    data.ideas.forEach((idea) => {
      map[idea._id!] = idea;
    });
    return map;
  }, [data.ideas]);

  const selectedIdeas = useMemo(() => {
    return _selectedIdeas.map((i) => {
      const matchedIdea = ideaMap[i._id!];
      return matchedIdea || i;
    });
  }, [ideaMap, _selectedIdeas]);

  const [selectingMultipleCards, setSelectingMultipleCards] = useState(false);

  const theme = useTheme();

  const params = new SearchParams(window.location.search);
  const paramIdeaId = params.get("idea");
  const paramViewType = params.get("queryView");

  useEffect(() => {
    if (
      paramViewType &&
      IdeaViewTypes.map((v) => v.value).includes(paramViewType)
    ) {
      setIdeaViewType(paramViewType, false);
    }
  }, [paramViewType, setIdeaViewType]);

  // For project board view, we only want to paginate within the lane of the currently previewed idea
  const paginatedIdeas = useMemo(() => {
    if (ideaViewType !== "board") return data.ideas;
    if (!previewId) return data.ideas;
    const previewIdea = ideaMap[previewId];
    const currentLaneId = previewIdea?.projectManagement?.boards?.find(
      (b) => b.forId === challengeId,
    )?.lane;
    const ideasInLane = data.ideas.filter(
      (i) =>
        i.projectManagement?.boards?.find((b) => b.forId === challengeId)
          ?.lane === currentLaneId,
    );
    // Now sort by order in lane
    const sortedIdeasInLane = ideasInLane.sort((a, b) => {
      const aOrder = a.projectManagement?.boards?.find(
        (b) => b.forId === challengeId,
      )?.order;
      const bOrder = b.projectManagement?.boards?.find(
        (b) => b.forId === challengeId,
      )?.order;
      return (aOrder || 0) - (bOrder || 0);
    });
    return sortedIdeasInLane;
  }, [ideaViewType, data.ideas, ideaMap, previewId, challengeId]);

  const canGoPrevIdea = useMemo(
    () =>
      paginatedIdeas.findIndex((i) => i._id === previewId) > 0 ||
      data.previousPageAvailable,
    [paginatedIdeas, data.previousPageAvailable, previewId],
  );
  const canGoNextIdea = useMemo(
    () =>
      paginatedIdeas.findIndex((i) => i._id === previewId) !==
        paginatedIdeas.length - 1 || data.nextPageAvailable,
    [paginatedIdeas, data.nextPageAvailable, previewId],
  );

  const currentPreviewIdInList = useMemo(
    () => data.ideas.some((i) => i._id === previewId),
    [data.ideas, previewId],
  );
  const template = challenge.ownerIdeaTemplate;
  const assessmentRounds = useMemo(
    () => template?.assessments?.rounds || [],
    [template],
  );

  const { ideaFilter } = useAppSelector((state) => state.challenges);
  const updateSearchTerm = useCallback(
    (term) => {
      updateFilters({ ideaFilter: term, ideaPage: 1 });
    },
    [updateFilters],
  );

  useEffect(() => {
    setAssessmentAssignIdeas([]);
    setAssessmentAssignRoundId("");
    setChallengeChooserIdea("");
    setSelectedIdeas([]);
  }, []);

  const openIdeaPreview = useCallback((ideaId) => {
    setPreviewId(ideaId);
  }, []);

  useDidMount(() => {
    setPreviewId(paramIdeaId || "");
  });

  useEffect(() => {
    if (ideaViewType !== "board") {
      setSelectingMultipleCards(false);
    }
  }, [ideaViewType]);

  const movePreviewIdea = useCallback(
    (amount) => {
      const ideaIndex = paginatedIdeas.findIndex((i) => i._id === previewId);
      if (ideaIndex === -1) return;
      const newIndex = ideaIndex + amount;
      if (newIndex === -1 && data.previousPageAvailable) {
        // Set page to (current page - 1)
        // Set postFetchPreviewIndex to (limit - 1)
        updateFilters({ ideaPage: page - 1, postFetchPreviewIndex: limit - 1 });
      } else if (newIndex === data.ideas.length && data.nextPageAvailable) {
        // Set page to (current page + 1)
        // Set postFetchPreviewIndex to (0)
        updateFilters({ ideaPage: page + 1, postFetchPreviewIndex: 0 });
      } else {
        const newPreviewId = paginatedIdeas.map((i) => i._id!)[newIndex];
        setPreviewId(newPreviewId);
      }
    },
    [data, previewId, page, limit, updateFilters, paginatedIdeas],
  );

  const onSelectIdea = useCallback((idea) => {
    setSelectedIdeas((prev) => {
      const prevIndex = prev.findIndex((i) => i._id === idea._id);
      if (prevIndex === -1) {
        return [...prev, idea];
      }
      prev.splice(prevIndex, 1);
      return [...prev];
    });
  }, []);

  const pinIdea = useCallback(
    (ideaId, isPinned) => {
      api.ideas.updateStatus(
        ideaId,
        { isPinned },
        () => {
          toast.success(
            isPinned ?
              `${t("common:capitalise", { key: "generic.idea" })} pinned`
            : `${t("common:capitalise", { key: "generic.idea" })} unpinned`,
          );

          api.setQueryData(
            api.challenges.getIdeas,
            [challengeId],
            ((prev: any) => ({
              ...(prev || {}),
              ideas: (prev?.ideas || []).map((idea) =>
                idea._id === ideaId ? { ...idea, isPinned } : idea,
              ),
            })) as any,
            { allowUndefined: true },
          );
          api.invalidateQueries(api.challenges.getIdeas, [challengeId]);
        },
        () => {
          toast.error("Failed to pin idea");
        },
      );
    },
    [challengeId, t],
  );

  const bulkUpdateStatus = useCallback(
    (selectedIdeas, statusKey, status) => {
      api.challenges.updateIdeasStatus(
        challengeId,
        { ideas: selectedIdeas, [statusKey]: status },
        ({ ideas: updatedIdeas }) => {
          toast(`${selectedIdeas.length} ${t("generic.ideas")} updated`);
          api.setQueryData(api.challenges.getIdeas, [challengeId], (prev) => ({
            ...prev,
            ideas: prev.ideas.map((idea) => {
              const updatedIdea = updatedIdeas.find((u) => u._id === idea._id);
              if (!updatedIdea) return idea;
              return { ...idea, ...updatedIdea };
            }),
          }));
          // Requires full refetch to update pinned ideas which are now bubbled to top
          api.invalidateQueries(api.challenges.getIdeas, [challengeId]);
        },
        () => {},
      );
    },
    [challengeId, t],
  );

  const bulkMoveIdeas = useCallback(
    (ideaIds, newChallengeId, onComplete = () => {}) => {
      api.ideas.moveToChallenge(
        ideaIds,
        newChallengeId,
        () => {
          toast.success(
            `${t("common:capitalise", { key: "generic.ideas" })} moved to ${t("generic.challenge")}`,
          );
          setSelectedIdeas([]);

          api.invalidateQueries(api.challenges.getIdeas, [challengeId]);
          api.invalidateQueries(api.challenges.getIdeas, [newChallengeId]);
          for (const id of ideaIds) {
            api.invalidateQueries(api.ideas.get, [id]);
          }
          if (onComplete) onComplete();
        },
        (err) => toast.error(err.message),
      );
    },
    [t, challengeId],
  );

  // Add label to idea, with choice of whether to apply or remove
  const labelIdea = useCallback(
    (ideaId, label, isApplied) => {
      const originalIdeas = [...data.ideas];
      const idea = originalIdeas.find((i) => i._id === ideaId);
      if (!idea) {
        return;
      }
      const { labels = [] } = idea;
      const existingIndex = labels.indexOf(label);

      if (isApplied && existingIndex === -1) labels.push(label);
      if (!isApplied && existingIndex > -1) labels.splice(existingIndex, 1);

      api.ideas.updateStatus(
        ideaId,
        { labels },
        () => {
          api.setQueryData(api.challenges.getIdeas, [challengeId], (prev) => ({
            ...prev,
            ideas: prev.ideas.map((idea) => {
              if (idea._id !== ideaId) return idea;
              return { ...idea, labels };
            }),
          }));
        },
        () => {
          toast.error("Failed to apply label");
        },
      );
    },
    [challengeId, data.ideas],
  );

  const voteForIdea = useCallback(
    (ideaId, addVote) => {
      api.ideas.vote(
        ideaId,
        addVote,
        ({ addedVotesFor, removedVotesFor }) => {
          onFollow("challenge", challengeId);
          const countIdeasVoted = addedVotesFor.length - removedVotesFor.length;

          const idea = data.ideas.find((i) => i._id === ideaId);
          if (!idea) return;

          util.onUpdateIdea(idea, {
            voteCount: (idea?.voteCount || 0) + (addVote ? 1 : -1),
            votedFor: addVote,
          });
          api.setQueryData(api.challenges.getIdeas, [challengeId], (prev) => ({
            ...prev,
            ideasVotedFor: (prev?.ideasVotedFor || 0) + countIdeasVoted,
          }));
        },
        (err) => {
          toast.error(err.message);
        },
      );
    },
    [challengeId, onFollow, data.ideas],
  );

  const addTag = useCallback(
    (idea, newTag) => {
      api.tags.tagSingle(
        "ideas",
        newTag._id,
        idea._id,
        (updatedTag) => {
          util.onUpdateIdea(idea, {
            tags: [...(idea?.tags || []), updatedTag._id],
            ownerTags: [...(idea?.ownerTags || []), updatedTag],
          });

          // Invalidate challenge to reload tag list
          api.invalidateQueries(api.challenges.get, [challengeId]);
        },
        () => {},
      );
    },
    [challengeId],
  );

  const unTag = useCallback(
    (idea, tag) => {
      api.tags.untagSingle(
        "ideas",
        tag._id,
        idea._id,
        (updatedTag) => {
          util.onUpdateIdea(idea, {
            tags: (idea?.tags || []).filter((t) => t !== updatedTag._id),
            ownerTags: (idea?.ownerTags || []).filter(
              (t) => t._id !== updatedTag._id,
            ),
          });

          // Invalidate challenge to reload tag list
          api.invalidateQueries(api.challenges.get, [challengeId]);
        },
        () => {},
      );
    },
    [challengeId],
  );

  const createChallengeFromIdea = useCallback(
    (ideaId) => {
      api.challenges.createFromIdea(
        { idea: ideaId },
        (data) => {
          navigate(`/challenges/${data.challenge._id}`);
          toast.success("Challenge created");
        },
        (err) => toast.error(err.message),
      );
    },
    [navigate],
  );

  const removeIdeaFromMerge = useCallback(
    (
      mergeIdeaId: string,
      ideaId: string,
      onComplete = (_mergeIdeaId: string, _ideaId: string) => {},
      onError = (_mergeIdeaId: string, _ideaId: string) => {},
    ) => {
      // If you're removing the 2nd to last idea from a merge, then we delete the merge container, as there'd be no point in having it
      const mergeIdea = data.ideas.find((i) => i._id === mergeIdeaId);
      const willDeleteMergeContainer =
        mergeIdea?.children && mergeIdea.children.length <= 2;
      util
        .confirm(
          `Are you sure you want to remove this ${t("generic.idea")} from the merge?`,
          `The ${t("generic.idea")} will not be deleted and will remain in the ${t("generic.challenge")}. You can re-add it to the merge at any time. ${willDeleteMergeContainer ? `The merge container will be deleted as it will only contain one ${t("generic.idea")}.` : ""}`,
        )
        .then(() => {
          setRemovingIdeaFromMerge(ideaId);
          api.ideas.removeIdeaFromMerge(
            mergeIdeaId,
            ideaId,
            () => {
              if (willDeleteMergeContainer) {
                // If we delete the merge container, we get the ID of the idea that wasn't deleted, and change the preview ID to that
                const nonDeletedIdeaId = mergeIdea?.children?.find(
                  (c) => c !== ideaId,
                );
                if (nonDeletedIdeaId) {
                  setPreviewId(nonDeletedIdeaId);
                }
              }
              setRemovingIdeaFromMerge("");
              api.invalidateQueries(api.challenges.getIdeas, [challengeId]);
              api.invalidateQueries(api.ideas.get, [ideaId]);
              api.invalidateQueries(api.ideas.get, [mergeIdeaId]);
              onComplete(mergeIdeaId, ideaId);
            },
            (err) => {
              toast.error(err.message);
              setRemovingIdeaFromMerge("");
              onError(mergeIdeaId, ideaId);
            },
          );
        })
        .catch(() => {});
    },
    [challengeId, data.ideas, t],
  );

  const updateProjectLane = useCallback(
    (
      ideaId: string,
      targetLaneId: string,
      position: number = 0,
      announcementIds: Array<string>,
    ) => {
      const idea = data.ideas.filter((i) => i._id === ideaId)[0];
      const board = idea?.projectManagement?.boards?.find(
        (b) => b.forId === idea.challenge,
      );
      if (
        !canManageChallenge &&
        !(board?.assignees && board.assignees.indexOf(user._id) > -1) &&
        !util.hasPermission(user, "challenge.editProjectBoard", challengeId)
      ) {
        return false;
      }
      // If no new lane or order, then return:
      if (
        !(
          (board?.lane || "default") !== targetLaneId ||
          position !== board?.order ||
          0
        )
      ) {
        return;
      }

      api.boards.updateProjectIdea(
        challengeId,
        ideaId,
        targetLaneId,
        { order: position, sendAnnouncements: announcementIds },
        ({ idea: updatedIdea, updatedIdeas }) => {
          api.setQueryData(api.challenges.getIdeas, [challengeId], (prev) => ({
            ...prev,
            ideas: prev.ideas.map((i) => {
              const updatedIdea = updatedIdeas.find((u) => u._id === i._id);
              if (updatedIdea) {
                return {
                  ...i,
                  projectManagement: updatedIdea.projectManagement,
                };
              }
              return i;
            }),
          }));
          api.setQueryData(api.ideas.get, [ideaId], (prev) => ({
            ...prev,
            projectManagement: updatedIdea.projectManagement,
          }));
          // Refetch required for project lane impacts
          api.invalidateQueries(api.challenges.getIdeas, [challengeId]);
          toast.success(
            `${t("common:capitalise", { key: "generic.idea" })} moved`,
          );
        },
        () => {},
      );
    },
    [data.ideas, user, canManageChallenge, challengeId, t],
  );

  const updateProjectLaneMulti = useCallback(
    (ideaIds: string[], targetLane: string, announcementIds: Array<string>) => {
      api.boards.updateProjectIdeasMulti(
        challengeId,
        { targetLane, ideaIds, sendAnnouncements: announcementIds },
        ({ updatedIdeas }) => {
          toast.success(
            `Moved ${util.pluralise(ideaIds.length, t("generic.idea"), t("generic.ideas"), true)}`,
          );
          setSelectedIdeas([]);
          api.setQueryData(api.challenges.getIdeas, [challengeId], (prev) => ({
            ...prev,
            ideas: prev.ideas.map((idea) => {
              const updatedIdea = updatedIdeas?.find((i) => i._id === idea._id);
              if (updatedIdea) {
                return {
                  ...idea,
                  projectManagement: updatedIdea.projectManagement,
                };
              }
              return idea;
            }),
          }));
          // Refetch required for project lane impacts
          api.invalidateQueries(api.challenges.getIdeas, [challengeId]);
        },
        (err) => {
          toast.error(`Unable to move ${t("generic.ideas")}. ${err.message}`);
        },
      );
    },
    [challengeId, t],
  );

  const checkUpdateProjectLane = useCallback(
    (ideaId: string, laneId: string, position: number = 0) => {
      const newLane = projectLaneQuery.data?.lanes.find(
        (l) => l._id === laneId,
      );
      if (!newLane?.announcements) {
        updateProjectLane(ideaId, laneId, position, []);
      } else {
        setLaneAnnouncements(newLane.announcements);
        setConfirmAnnouncementModalOpen(true);
        setLaneUpdateContext({ laneId, ideaId });
      }
    },
    [projectLaneQuery.data, updateProjectLane],
  );

  const checkUpdateProjectLaneMulti = useCallback(
    (ideaIds: string[], laneId: string) => {
      const newLane = projectLaneQuery.data?.lanes.find(
        (l) => l._id === laneId,
      );
      if (!newLane?.announcements) {
        updateProjectLaneMulti(ideaIds, laneId, []);
      } else {
        setLaneAnnouncements(newLane.announcements);
        setConfirmAnnouncementModalOpen(true);
        setLaneUpdateContext({ laneId, ideaIds });
      }
    },
    [projectLaneQuery.data, updateProjectLaneMulti],
  );

  const updateIdeaProjectDeadlineMulti = useCallback(
    (deadline: Date) => {
      const formattedDeadline = moment(deadline, "YYYY-MM-DD").toDate();
      api.boards.updateProjectIdeasDeadline(
        challengeId,
        {
          deadline: formattedDeadline,
          ideaIds: selectedIdeas.map((i) => i._id),
        },
        ({ updatedIdeas }) => {
          api.setQueryData(api.challenges.getIdeas, [challengeId], (prev) => ({
            ...prev,
            ideas: prev.ideas.map((idea) => {
              const updatedIdea = updatedIdeas.find((i) => i._id === idea._id);
              if (!updatedIdea) return idea;

              return {
                ...idea,
                projectManagement: updatedIdea.projectManagement,
              };
            }),
          }));
          toast.success(
            `Updated deadline for ${util.pluralise(updatedIdeas.length, t("generic.idea"), t("generic.ideas"), true)}`,
          );
        },
        (err) => {
          toast.error(`Unable to update deadline. ${err.message}`);
        },
      );
    },
    [challengeId, selectedIdeas, t],
  );

  const bulkProjectAssign = useCallback(
    (userIds: string[]) => {
      api.boards.bulkAssignProjectIdeas(
        challengeId,
        { ideaIds: selectedIdeas.map((i) => i._id), userIds },
        ({ updatedIdeas, allAssignees }) => {
          api.setQueryData(api.challenges.getIdeas, [challengeId], (prev) => ({
            ...prev,
            ideas: prev.ideas.map((idea) => {
              const updatedIdea = updatedIdeas.find((i) => i._id === idea._id);
              if (!updatedIdea) return idea;
              return {
                ...idea,
                projectManagement: updatedIdea.projectManagement,
              };
            }),
          }));
          setSelectedIdeas([]);
          toast.success(
            `Assigned ${util.pluralise(userIds.length, "user", "users", true)} for ${util.pluralise(updatedIdeas.length, t("generic.idea"), t("generic.ideas"), true)}`,
          );
        },
        (err) => {
          toast.error(err.message);
        },
      );
    },
    [challengeId, selectedIdeas, t],
  );

  const RenderIdeaActions = useCallback(
    (ideaIds, isBulk = false) => {
      if (!challenge) return null;
      if (ideaIds.length === 0) return null;

      const usingIdeas =
        isBulk ? selectedIdeas
        : ideaMap[ideaIds[0]] ? [ideaMap[ideaIds[0]]]
        : [];
      if (usingIdeas.length === 0) return null;

      // You can only merge ideas, where either:
      // - None of the ideas have been merged
      // - One of the ideas is a merge parent, and the rest of the selected ideas have not been merged/are not merge parents
      const canMerge =
        isBulk &&
        (selectedIdeas.filter((i) => i.parent || i.children).length === 0 ||
          (selectedIdeas.filter((i) => i.children).length === 1 &&
            selectedIdeas.filter((i) => !(i.parent || i.children)).length ===
              selectedIdeas.length - 1));

      return (
        <ActionPopup>
          <Menu secondary vertical>
            {(
              ideaViewType === "board" &&
              (canManageChallenge ||
                util.hasPermission(
                  user,
                  "challenge.moveProjectIdeas",
                  challengeId,
                ) ||
                util.hasPermission(
                  user,
                  "challenge.editProjectAssignees",
                  challengeId,
                ) ||
                util.hasPermission(
                  user,
                  "challenge.editProjectDates",
                  challengeId,
                ))
            ) ?
              <>
                {(
                  canManageChallenge ||
                  util.hasPermission(
                    user,
                    "challenge.moveProjectIdeas",
                    challengeId,
                  )
                ) ?
                  <Dropdown
                    item
                    icon={"move"}
                    text={`Move all ${t("generic.ideas")} to project lane`}
                    onClick={() => {
                      setActionPopupOpen(false);
                      setMoveLaneIdeas(selectedIdeas);
                    }}
                  />
                : null}
                {(
                  canManageChallenge ||
                  util.hasPermission(
                    user,
                    "challenge.editProjectAssignees",
                    challengeId,
                  )
                ) ?
                  <Menu.Item
                    content={`Project assign users to all ${t("generic.ideas")}`}
                    onClick={() => {
                      setProjectAssignIdeas(ideaIds);
                      setActionPopupOpen(false);
                    }}
                  />
                : null}
                {(
                  canManageChallenge ||
                  util.hasPermission(
                    user,
                    "challenge.editProjectDates",
                    challengeId,
                  )
                ) ?
                  <Menu.Item
                    icon="calendar outline"
                    content="Set management deadline"
                    onClick={() => {
                      setManagementDeadlineIdeas(usingIdeas);
                      setActionPopupOpen(false);
                    }}
                  ></Menu.Item>
                : null}
              </>
            : null}
            {(
              canManageChallenge ||
              util.hasPermission(user, "challenge.pinIdeas", challengeId)
            ) ?
              <Popup
                on="hover"
                position="right center"
                content={<PinIdeaHelper challenge={challenge} />}
                trigger={
                  <Menu.Item
                    icon="pin"
                    content={
                      isBulk ? "Pin"
                      : usingIdeas[0].isPinned ?
                        "Unpin"
                      : "Pin"
                    }
                    onClick={() =>
                      !isBulk ?
                        pinIdea(usingIdeas[0]._id, !usingIdeas[0]?.isPinned)
                      : bulkUpdateStatus(
                          ideaIds,
                          "isPinned",
                          !usingIdeas.every((i) => i.isPinned),
                        )
                    }
                  />
                }
              />
            : null}
            {(
              canManageChallenge ||
              util.hasPermission(
                user,
                "challenge.editIdeaDeadlines",
                challengeId,
              )
            ) ?
              <Menu.Item
                icon="calendar"
                content="Set submission deadline"
                onClick={() => {
                  setActionPopupOpen(false);
                  setDeadlineIdeas(usingIdeas);
                }}
              />
            : null}
            {(
              util.hasPermission(
                user,
                "org.addToGroupsDirectly",
                user?.ownerOrganisation?._id,
              )
            ) ?
              <Menu.Item
                icon="users"
                content="Add authors to group"
                onClick={() => {
                  setActionPopupOpen(false);
                  setGroupAddIdeas(usingIdeas);
                }}
              />
            : null}
            {(
              canManageChallenge ||
              util.hasPermission(user, "challenge.editSettings", challengeId)
            ) ?
              <Menu.Item
                content={`Create next-phase ${t("generic.challenge")} from ${t("generic.ideas")}`}
                onClick={() => {
                  setActionPopupOpen(false);
                  setNextPhaseChallenge(ideaIds);
                }}
              />
            : null}
            {(
              ideaMergeEnabled &&
              (canManageChallenge ||
                util.hasPermission(user, "challenge.mergeIdeas", challengeId))
            ) ?
              <Popup
                on="hover"
                content={
                  canMerge ? `Merge ${t("generic.ideas")} together`
                  : selectedIdeas.length > 1 ?
                    `You can only merge ${t("generic.ideas")} where none of the previous ${t("generic.ideas")} have been merged`
                  : `Select at least two ${t("generic.ideas")} to merge`
                }
                trigger={
                  <Menu.Item
                    disabled={selectedIdeas.length < 2 || !canMerge}
                    icon="sitemap"
                    content="Merge"
                    onClick={() => {
                      setActionPopupOpen(false);
                      // @ts-ignore
                      confirmMerge(
                        selectedIdeas,
                        projectLaneQuery.data?.lanes,
                        challenge,
                      )
                        .then(() => {
                          setSelectedIdeas([]);
                          api.invalidateQueries(api.challenges.getIdeas, [
                            challengeId,
                          ]);
                          api.invalidateQueries(api.ideas.get, []);
                        })
                        .catch(() => {});
                    }}
                  />
                }
              />
            : null}
            {(
              challenge.ownerIdeaTemplate?.assessmentEnabled &&
              (canManageChallenge ||
                util.hasPermission(
                  user,
                  "challenge.editAssessments",
                  challengeId,
                )) &&
              assessmentRounds.length > 0
            ) ?
              assessmentRounds.length > 1 ?
                <Dropdown
                  item
                  icon="user plus"
                  text="Assign assessors"
                  pointing="right"
                >
                  <DropdownMenu style={{ right: "100%", left: "auto" }}>
                    {assessmentRounds.map((round) => (
                      <DropdownItem
                        key={round.id}
                        content={`Assign to ${round.name}`}
                        onClick={() => {
                          setActionPopupOpen(false);
                          setAssessmentAssignRoundId(round.id);
                          setAssessmentAssignIdeas(ideaIds);
                        }}
                      />
                    ))}
                  </DropdownMenu>
                </Dropdown>
              : <Menu.Item
                  icon="user plus"
                  content="Assign assessors"
                  onClick={() => {
                    setActionPopupOpen(false);
                    setAssessmentAssignRoundId(assessmentRounds[0].id);
                    setAssessmentAssignIdeas(ideaIds);
                  }}
                />

            : null}
            {(
              ideaViewType !== "board" &&
              (canManageChallenge ||
                util.hasPermission(
                  user,
                  "challenge.moveIdeasToChallenge",
                  challengeId,
                ))
            ) ?
              <>
                <Menu.Item
                  icon="arrow right"
                  content={`Move to another ${t("generic.challenge")}`}
                  onClick={() => {
                    setActionPopupOpen(false);
                    setChallengeChooserIdea(ideaIds);
                  }}
                />
                {!isBulk && usingIdeas[0].challengeCreation && (
                  <Menu.Item
                    icon="target"
                    content={`Create ${t("generic.challenge")} from ${t("generic.idea")}`}
                    onClick={() => createChallengeFromIdea(usingIdeas[0]._id)}
                  />
                )}
              </>
            : null}

            <IdeaPdfExport
              hidePopup
              ideas={usingIdeas}
              challenge={challenge as unknown as OpenAPI.Schemas["Challenge"]}
              trigger={
                <Menu.Item icon="pdf file outline" content="Export as PDF" />
              }
            />

            {(
              canManageChallenge ||
              util.hasPermission(user, "challenge.editIdeaLabels", challengeId)
            ) ?
              <Menu.Item
                icon="bookmark"
                content="Apply a label"
                onClick={() => setLabelIdeas(usingIdeas)}
              />
            : null}
            {isBulk && (
              <Menu.Item
                icon="ban"
                content="Deselect all"
                onClick={() => setSelectedIdeas([])}
              />
            )}
            {(
              canManageChallenge ||
              util.hasPermission(user, "challenge.deleteIdeas", challengeId)
            ) ?
              <Popup
                disabled={!(!isBulk && !!usingIdeas[0]?.children)}
                content={`Delete this merged ${t("generic.idea")}. None of the contained ${t("generic.ideas")} will be deleted, and they will be returned to the ${t("generic.idea")} list.`}
                trigger={
                  <Menu.Item
                    icon="trash"
                    content={
                      !isBulk && !!usingIdeas[0]?.children ?
                        `Delete merged ${t("generic.idea")}`
                      : `Delete ${t("generic.idea")}${!isBulk ? "" : "s"}`
                    }
                    onClick={() => {
                      setActionPopupOpen(false);
                      setDeletingIdeas(usingIdeas);
                    }}
                  />
                }
              />
            : null}
          </Menu>
        </ActionPopup>
      );
    },
    [
      challenge,
      ideaViewType,
      ideaMergeEnabled,
      selectedIdeas,
      canManageChallenge,
      ideaMap,
      user,
      pinIdea,
      challengeId,
      createChallengeFromIdea,
      bulkUpdateStatus,
      t,
      projectLaneQuery.data?.lanes,
      assessmentRounds,
    ],
  );

  const hasPinnedIdea = useMemo(
    () => data.ideas?.some((i) => i.isPinned),
    [data.ideas],
  );

  const RenderIdeaCard = useCallback(
    (idea) => (
      // @ts-ignore
      <IdeaCard
        key={idea._id}
        idea={idea}
        ideasVotedFor={data.ideasVotedFor || 0}
        challenge={challenge}
        addTag={(tag) => addTag(idea, tag)}
        unTag={(tag) => unTag(idea, tag)}
        voteForIdea={voteForIdea}
        listView={ideaViewType === "list"}
        labelIdea={labelIdea}
        onSelectIdea={onSelectIdea}
        selectedIdeas={selectedIdeas.map((i) => i._id)}
        ideaActions={RenderIdeaActions}
        onUpdateIdea={(idea) => {}}
        pinIdea={pinIdea}
        setPreviewOpen={openIdeaPreview}
        hasPinnedIdea={hasPinnedIdea}
        updateProjectLane={checkUpdateProjectLane}
        loading={fullIdeaQuery.isLoading}
      />
    ),
    [
      data.ideasVotedFor,
      fullIdeaQuery.isLoading,
      challenge,
      ideaViewType,
      voteForIdea,
      labelIdea,
      onSelectIdea,
      selectedIdeas,
      RenderIdeaActions,
      pinIdea,
      openIdeaPreview,
      hasPinnedIdea,
      checkUpdateProjectLane,
      addTag,
      unTag,
    ],
  );

  const usedVotes = parseInt(challenge?.voteLimit, 10) === data.ideasVotedFor;
  const votesRemaining =
    parseInt(challenge?.voteLimit, 10) - data.ideasVotedFor;

  const isFullSize = !theme.sizes.isMobile;

  const activeIdeaViewType = useMemo(
    () => IdeaViewTypes.find((viewType) => viewType.key === ideaViewType),
    [ideaViewType],
  );

  const searchComponent = useMemo(
    () => (
      <Input
        size="small"
        placeholder={
          canManageChallenge || canViewProjectBoard ?
            `${t("challenge.ideas.filter.admin.search")}...`
          : `${t("challenge.ideas.filter.search")}...`
        }
        icon="search"
        value={ideaFilter}
        onChange={(e) => updateSearchTerm(e.target.value)}
        style={{
          height: 34,
          flex: 1,
          maxWidth: theme.sizes.isMobile ? undefined : 350,
        }}
        fluid
      />
    ),
    [
      canManageChallenge,
      canViewProjectBoard,
      ideaFilter,
      theme.sizes.isMobile,
      updateSearchTerm,
      t,
    ],
  );

  const numberOfFiltersNotDefault = useMemo(
    () =>
      ChallengeFilterKeys.filter((key) => {
        // If we're on the board view, we don't want to count the ideaOrder filter as a filter since we hide the filter option and it can't be changed
        if (ideaViewType === "board" && key === "ideaOrder") {
          return false;
        }
        return !util.variablesAreEqual(
          challengesReducer[key],
          challengeReducerDefaultState[key],
        );
      }).length,
    [challengesReducer, ideaViewType],
  );

  return (
    <StyledIdeasContainer>
      <ConfirmAnnouncementModal
        onClose={() => setConfirmAnnouncementModalOpen(false)}
        modalOpen={confirmAnnouncementModalOpen}
        ideaCount={
          laneUpdateContext.ideaId ? 1
          : laneUpdateContext.ideaIds ?
            laneUpdateContext.ideaIds.length
          : 0
        }
        announcements={laneAnnouncements}
        handleConfirm={(announcementIds) => {
          setConfirmAnnouncementModalOpen(false);
          if (laneUpdateContext.ideaId && laneUpdateContext.laneId) {
            updateProjectLane(
              laneUpdateContext.ideaId,
              laneUpdateContext.laneId,
              0,
              announcementIds,
            );
          } else if (laneUpdateContext.ideaIds && laneUpdateContext.laneId) {
            updateProjectLaneMulti(
              laneUpdateContext.ideaIds,
              laneUpdateContext.laneId,
              announcementIds,
            );
          }
          setLaneUpdateContext({});
        }}
      />
      <ProductTour
        tour="projectBoardOnIdeasPage"
        isReady={
          !!challenge &&
          ideaViewType === "board" &&
          window.location.pathname.indexOf("/ideas") > -1
        }
      />

      <ChallengeIdeaFilters
        // @ts-ignore
        challenge={challenge}
        popularTags={ideaFilterQuery.data?.popularTags}
        allLabels={ideaFilterQuery.data?.allLabels}
        availableGroups={ideaFilterQuery.data?.availableGroups}
        availableOrganisations={ideaFilterQuery.data?.availableOrganisations}
        availableSortParameters={ideaFilterQuery.data?.availableSortParameters}
        availableFilterParameters={
          ideaFilterQuery.data?.availableFilterParameters
        }
        allProjectAssignees={ideaFilterQuery.data?.allProjectAssignees}
        availableImpacts={ideaFilterQuery.data?.availableImpacts}
      />

      <IdeaDeletionModal
        challengeId={challengeId}
        ideasToDelete={deletingIdeas}
        setIdeasToDelete={setDeletingIdeas}
        setSelectedIdeas={setSelectedIdeas}
      />
      <IdeaDeadlineModal
        challengeId={challengeId}
        deadlineIdeas={deadlineIdeas}
        setDeadlineIdeas={setDeadlineIdeas}
      />
      <IdeaManagementDeadlineModal
        managementDeadlineIdeas={managementDeadlineIdeas}
        setManagementDeadlineIdeas={setManagementDeadlineIdeas}
        updateIdeaProjectDeadlineMulti={updateIdeaProjectDeadlineMulti}
      />
      <IdeaMoveToLaneModal
        moveLaneIdeas={moveLaneIdeas}
        setMoveLaneIdeas={setMoveLaneIdeas}
        allChallengeProjectLanes={projectLaneQuery.data?.lanes || []}
        checkUpdateProjectLaneMulti={checkUpdateProjectLaneMulti}
        challenge={challenge}
      />
      <GroupUserManagementModal
        users={[
          ...new Set(
            groupAddIdeas.flatMap((idea) => {
              const userIds = [];
              if (idea.user) {
                userIds.push(idea.user);
              }
              if (idea.collaborators) {
                userIds.push(...idea.collaborators);
              }
              return userIds;
            }),
          ),
        ]}
        onClose={() => setGroupAddIdeas([])}
      />
      <IdeaPreview
        totalIdeas={
          ideaViewType === "board" ? paginatedIdeas.length : data.total
        }
        currentIdeaPosition={
          paginatedIdeas.findIndex((i) => i._id === previewId) + 1
        }
        ideaId={previewId}
        assessmentEnabled={challenge.ownerIdeaTemplate?.assessmentEnabled}
        closeModal={() => {
          setPreviewId("");
        }}
        page={page}
        limit={limit}
        moveIdea={currentPreviewIdInList && movePreviewIdea}
        canGoNextIdea={canGoNextIdea}
        canGoPrevIdea={canGoPrevIdea}
        removeIdeaFromMerge={removeIdeaFromMerge}
        removingIdeaFromMerge={removingIdeaFromMerge}
        challenge={challenge}
        updateProjectLane={checkUpdateProjectLane}
      />
      <LabelChooser
        isOpen={labelIdeas.length > 0}
        onClose={() => setLabelIdeas([])}
        challengeId={challengeId}
        existing={[...new Set(labelIdeas.flatMap((i) => i.labels || []))]}
        onComplete={(label) =>
          api.challenges.addIdeasLabels(
            challengeId,
            { ideas: labelIdeas.map((i) => i._id!), labels: [label] },
            ({ ideas: updatedIdeas }) => {
              api.setQueryData(
                api.challenges.getIdeas,
                [challengeId],
                (prev) => ({
                  ...prev,
                  ideas: prev.ideas.map((i) => {
                    const updatedIdea = updatedIdeas?.find(
                      (u) => u._id === i._id,
                    );
                    if (!updatedIdea) return i;
                    return { ...i, ...updatedIdea };
                  }),
                }),
              );
              setLabelIdeas((prevIdeas) =>
                prevIdeas.map((idea) => ({
                  ...idea,
                  labels: [
                    ...(idea.labels || []).filter((l) => l !== label),
                    label,
                  ],
                })),
              );
            },
            () => {},
          )
        }
        onRemoveExisting={(label) => {
          api.challenges.removeIdeasLabels(
            challengeId,
            { ideas: labelIdeas.map((i) => i._id!), labels: [label] },
            ({ ideas: updatedIdeas }) => {
              api.setQueryData(
                api.challenges.getIdeas,
                [challengeId],
                (prev) => ({
                  ...prev,
                  ideas: prev.ideas.map((i) => {
                    const updatedIdea = updatedIdeas?.find(
                      (u) => u._id === i._id,
                    );
                    if (!updatedIdea) return i;
                    return { ...i, ...updatedIdea };
                  }),
                }),
              );
              setLabelIdeas((prevIdeas) =>
                prevIdeas.map((idea) => ({
                  ...idea,
                  labels: (idea.labels || []).filter((l) => l !== label),
                })),
              );
            },
            () => {},
          );
        }}
      />
      {challenge && (
        <GatedChallengeModal
          openModal={nextPhaseChallenge.length > 0}
          closeModal={() => setNextPhaseChallenge([])}
          challengeName={challenge.name}
          challengeId={challenge._id}
          newGroupUsers={selectedIdeas.flatMap((idea) =>
            idea.collaborators ?
              [idea.user].concat(idea.collaborators)
            : [idea.user],
          )}
        />
      )}
      <UserChooser
        isOpen={projectAssignIdeas.length > 0}
        onClose={() => setProjectAssignIdeas([])}
        onComplete={(users) => bulkProjectAssign(users.map((u) => u._id!))}
        forType="projectAssignee"
        forId={challengeId}
        clearOnComplete
        enabledFeatures={{ search: true, invite: false }}
        subtitle={`Choose multiple users to project assign to ${t("generic.idea")}.`}
        confirm="Assign these users"
        searchFunction={(term, cb, fail) =>
          api.boards.searchProjectAssignees(
            challengeId,
            term,
            ({ users }) => cb(users),
            fail,
          )
        }
      />
      <UserChooser
        isOpen={assessmentAssignIdeas.length > 0}
        onClose={() => {
          setAssessmentAssignRoundId("");
          setAssessmentAssignIdeas([]);
        }}
        onComplete={(users) => {
          // @ts-ignore
          const emailUsers = users.filter((u) => u.isEmailInvitee);
          // @ts-ignore
          const existingUsers = users.filter((u) => !u.isEmailInvitee);
          if (emailUsers.length) {
            const ideaDetails = {};
            for (const ideaId of assessmentAssignIdeas) {
              ideaDetails[ideaId] = {
                assessmentRounds: [assessmentAssignRoundId],
              };
            }

            api.invitations.createBulk(
              {
                invitees: emailUsers.map((u) => u._id),
                invitationType: "email",
                forType: "ideaAssessor",
                forId: challengeId,
                forIdeas: assessmentAssignIdeas,
                ideaDetails,
              },
              () => {
                api.invalidateQueries(api.invitations.getForType, [
                  "ideaAssessor",
                  challengeId,
                ]);
                toast.success(
                  `Invitation${emailUsers.length > 1 ? "s" : ""} sent`,
                );
              },
              () => {},
            );
            api.setQueryData(
              api.challenges.getIdeas,
              [challengeId],
              (prev) => ({
                ...prev,
                ideas: prev.ideas.map((idea) => {
                  if (assessmentAssignIdeas.indexOf(idea._id) === -1)
                    return idea;
                  return {
                    ...idea,
                    invitedExternalAssessors: [
                      ...(idea.invitedExternalAssessors || []),
                      ...emailUsers.map((u) => u._id),
                    ],
                  };
                }),
              }),
            );
          }

          const filteredExistingUsers = existingUsers.filter(
            (u) => !u.isEmailInvitee,
          );
          if (!filteredExistingUsers.length) return;

          api.challenges.assignAssessors(
            challengeId,
            assessmentAssignIdeas,
            filteredExistingUsers.map((u) => u._id),
            assessmentAssignRoundId,
            ({ ideas: assessorIdeas }) => {
              api.setQueryData(
                api.challenges.getIdeas,
                [challengeId],
                (prev) => ({
                  ...prev,
                  ideas: prev.ideas.map((idea) => {
                    const updatedIdea = assessorIdeas.find(
                      (i) => i._id === idea._id,
                    );
                    if (!updatedIdea) return idea;
                    return {
                      ...idea,
                      assessments: updatedIdea.assessments,
                      assessmentDetails: updatedIdea.assessmentDetails,
                    };
                  }),
                }),
              );
              toast.success("Assessors assigned");
            },
            (err) => toast.error(err.message),
          );
        }}
        forType="assessor"
        forId={challengeId}
        afterContent={() =>
          !challenge.visibility?.organisations?.includes(user?.organisation) ?
            <AssessorAudienceWarning />
          : null
        }
        clearOnComplete
        enabledFeatures={{ search: true, invite: true }}
        subtitle={`Choose users to assign as assessors for ${assessmentAssignIdeas.length > 1 ? `the selected ${t("generic.ideas")}` : `the selected ${t("generic.idea")}`}. Assessors will be able to find the ${t("generic.ideas")} on their 'Assessments' tab within this ${t("generic.challenge")}.`}
        confirm="Assign these assessors"
        beforeContent={() => {
          const assessmentRound = assessmentRounds.find(
            (round) => round.id === assessmentAssignRoundId,
          );
          if (!assessmentRound) return null;
          const assessmentClosed =
            (assessmentRound.id === "legacy" ?
              challenge.ownerIdeaTemplate?.assessmentClosed
            : assessmentRound.assessmentClosed) || false;
          if (!assessmentClosed) return null;

          return (
            <Message warning>
              <Message.Header>Assessment closed</Message.Header>
              <Message.Content>
                Assessment is now closed for this {t("generic.challenge")}. If
                you assign assessors, they will not be able to complete their
                assessment. You can change this{" "}
                <Link to={`/challenges/${challenge._id}/settings/format`}>
                  here
                </Link>
                .
              </Message.Content>
            </Message>
          );
        }}
        searchFunction={(term, cb, fail) =>
          api.search.challengeAssessors(
            challengeId,
            term,
            ({ users }) => cb(users),
            fail,
          )
        }
        trigger={undefined}
      />
      {challenge._id && challengeChooserIdea?.length > 0 ?
        <ChallengeChooser
          isOpen={challengeChooserIdea.length > 0}
          onClose={() => setChallengeChooserIdea("")}
          single
          subtitle={`Find the challenge you want to move the ${util.pluralise(challengeChooserIdea?.length, t("generic.idea"), t("generic.ideas"), false)} to.`}
          ignores={[challenge._id]}
          onComplete={(c) =>
            bulkMoveIdeas(challengeChooserIdea, c[0]._id, () =>
              setChallengeChooserIdea(""),
            )
          }
          beforeContent={`We'll move the selected ${t("generic.idea")}(s) to another ${t("generic.challenge")} that you can manage.`}
          afterContent={
            <Message warning>
              <p>
                <Icon name="warning" />
                <b>Caution: </b>
                {`Moving ${t("generic.ideas")} to another ${t("generic.challenge")} will have the following side effects.`}
              </p>
              <ul>
                <li>
                  The {t("generic.idea")} will be removed from the original{" "}
                  {t("generic.challenge")}
                </li>
                <li>
                  The {t("generic.idea")} will keep the {t("generic.idea")}{" "}
                  format of the original {t("generic.challenge")}
                </li>
                <li>
                  {t("common:capitalise", { key: "generic.idea" })}{" "}
                  collaborators will be removed
                </li>
                <li>Assessments will be removed</li>
                <li>Project board assignees will be removed</li>
                <li>Project board notes will be removed</li>
              </ul>
            </Message>
          }
        />
      : null}
      {ideaViewType !== "board" ?
        <ChallengeMyIdeas
          challengeId={challenge._id}
          style={{ marginBottom: 20 }}
          showMyIdeas={showMyIdeas}
          setShowMyIdeas={setShowMyIdeas}
        />
      : null}
      {user ?
        <div style={{ marginBottom: theme.sizes.isMobile ? 0 : 10 }}>
          <FilterContainer>
            <StyledFilters style={{ flex: 1 }}>
              {ideaViewType !== "board" ?
                challenge.voteLimit &&
                challenge.votingVisibility === "users" && (
                  <Label>
                    {!usedVotes && <Icon name={"thumbs up"} />}{" "}
                    {util.pluralise(votesRemaining, "like", "likes")} remaining
                  </Label>
                )
              : null}

              <Button
                primary
                style={{ height: 32, marginRight: 0 }}
                icon="filter"
                size="tiny"
                content={`${
                  theme.sizes.isLargeComputer ?
                    filtersVisible ? `Hide filters`
                    : `Show filters`
                  : "Filters"
                }${numberOfFiltersNotDefault ? ` (${numberOfFiltersNotDefault})` : ""}`}
                onClick={() =>
                  updateFilters({ filtersVisible: !filtersVisible })
                }
              />

              {theme.sizes.isMobile ? null : searchComponent}

              {canManageChallenge || canActionIdeas ?
                ideaViewType === "board" ?
                  <Button
                    content={
                      selectingMultipleCards ?
                        "Exit selection mode"
                      : "Select multiple cards"
                    }
                    icon="grab"
                    size="tiny"
                    onClick={() => {
                      if (selectingMultipleCards) {
                        setSelectedIdeas([]);
                      }
                      setSelectingMultipleCards((prev) => !prev);
                    }}
                    style={{ width: 180, marginRight: 0 }}
                  />
                : <Button
                    size="tiny"
                    icon="check"
                    basic
                    content={t("generic.selectAll")}
                    onClick={() =>
                      setSelectedIdeas((prevSelectedIdeas) =>
                        prevSelectedIdeas.concat(
                          data.ideas.filter(
                            (idea) =>
                              !prevSelectedIdeas
                                .map((i) => i._id)
                                .includes(idea._id),
                          ),
                        ),
                      )
                    }
                  />

              : null}

              {basicIdeaQuery.isLoading ?
                <Loader active inline="centered" />
              : null}

              {selectedIdeas && selectedIdeas.length > 0 && (
                <Popup
                  on="click"
                  open={actionPopupOpen}
                  onOpen={() => setActionPopupOpen(true)}
                  onClose={() => setActionPopupOpen(false)}
                  trigger={
                    <Button
                      primary
                      icon="dropdown"
                      size="tiny"
                      content={util.pluralise(
                        selectedIdeas.length,
                        t("generic.idea"),
                        t("generic.ideas"),
                      )}
                    />
                  }
                  content={RenderIdeaActions(
                    selectedIdeas.map((idea) => idea._id),
                    true,
                  )}
                  style={{ padding: 5 }}
                />
              )}

              {ideaViewType === "board" ?
                (
                  canManageChallenge ||
                  util.hasPermission(
                    user,
                    "challenge.viewProjectBoard",
                    challenge._id,
                  )
                ) ?
                  <BoardEvents
                    forId={challengeId}
                    setCurrentIdeaId={setPreviewId}
                    buttonStyle={{ height: 32.5 }}
                    buttonProps={{ size: "tiny" }}
                  />
                : null
              : null}
            </StyledFilters>

            <StyledFilters>
              <Popup
                on="hover"
                content={`Change ${t("generic.idea")} view`}
                trigger={
                  <Dropdown
                    icon={null}
                    direction="left"
                    trigger={
                      <Button
                        icon={activeIdeaViewType?.icon}
                        content={activeIdeaViewType?.text}
                        size="tiny"
                        style={{ flexWrap: "nowrap", display: "flex" }}
                        className="project-board-ideas-page-1"
                      />
                    }
                    options={[
                      (
                        !theme.sizes.isLargeComputer &&
                        (util.canManageChallenge(user, challenge) ||
                          util.hasPermission(
                            user,
                            "challenge.editSettings",
                            challengeId,
                          ))
                      ) ?
                        {
                          key: "settings",
                          value: "settings",
                          as: Link,
                          to: `/challenges/${challenge._id}/settings/ideas`,
                          icon: "cogs",
                          text: `View ${t("generic.idea")} settings`,
                        }
                      : null,
                      ...IdeaViewTypes.filter((viewType) => {
                        if (viewType.key === "board") {
                          return (
                            challenge.projectManagementVisibility === "users" ||
                            canManageChallenge ||
                            util.hasPermission(
                              user,
                              "challenge.viewProjectBoard",
                              challengeId,
                            )
                          );
                        }
                        return true;
                      }).map(({ key, text, value, icon }) => ({
                        key,
                        value,
                        icon,
                        text: `${text} view`,
                      })),
                    ].filter((item) => Boolean(item))}
                    onChange={(_e, { value }) =>
                      setIdeaViewType(value as string)
                    }
                    selectOnBlur={false}
                  />
                }
              />
            </StyledFilters>
          </FilterContainer>
          {(
            ideaViewType === "board" &&
            filter === "submitted" &&
            theme.sizes.isComputer
          ) ?
            <span style={{ color: "#666", fontSize: 12 }}>
              Draft {t("generic.ideas")} are hidden by default in board view.
              You can change this in the {t("generic.idea")} filters.
            </span>
          : null}
          {theme.sizes.isMobile ?
            <div style={{ marginBottom: 5, width: "100%" }}>
              {searchComponent}
            </div>
          : null}
        </div>
      : null}
      {!user && (
        <Message>
          <h3>Please log in to view this page</h3>
          <p>
            Register or login to your account to track your own{" "}
            {t("generic.ideas")} or to view, comment, and like other{" "}
            {t("generic.ideas")} in this {t("generic.challenge")}.
          </p>
          <Divider section />
          <Button
            as={Link}
            to={`/login?then=/challenges/${challenge._id}/ideas`}
            primary
          >
            Login or register
          </Button>
        </Message>
      )}
      {(
        user &&
        (challenge.ideaVisibility === "users" ||
          canManageChallenge ||
          util.hasPermission(user, "challenge.viewIdeas", challengeId) ||
          (util.hasPermission(
            user,
            "challenge.viewProjectBoard",
            challengeId,
          ) &&
            ideaViewType === "board"))
      ) ?
        <StyledIdeaItemsContainer>
          {ideaViewType === "list" ?
            <IdeaListContainer>
              <colgroup>
                {/* @ts-ignore */}
                {isFullSize && (canManageChallenge || canActionIdeas) ?
                  <col width="40" height="55" />
                : null}
                {/* @ts-ignore */}
                <col width="35" height="55" />
                {coverImageEnabled && <col width="40" />}
                {/* @ts-ignore */}
                {isFullSize ?
                  <col height="55" />
                : null}
                <col />
                <col />
              </colgroup>
              <tbody>{data.ideas.map((idea) => RenderIdeaCard(idea))}</tbody>
            </IdeaListContainer>
          : null}
          {ideaViewType === "card" ?
            <Card.Group
              itemsPerRow={4}
              doubling
              stackable
              style={{ marginBottom: 20, zIndex: 5 }}
            >
              {data.ideas.map((idea) => RenderIdeaCard(idea))}
            </Card.Group>
          : null}
          {ideaViewType === "board" ?
            <ProjectBoard
              forId={challengeId!}
              ideas={data.ideas!}
              ideasTotal={data.total!}
              ideasLoading={fullIdeaQuery.isLoading}
              laneImpactMeasures={data.laneImpactMeasures}
              canManageChallenge={canManageChallenge}
              defaultLane={challenge.projectBoard?.defaultLane}
              projectManagementVisibility={
                challenge.projectManagementVisibility
              }
              previewId={previewId}
              setPreviewId={setPreviewId}
              selectionMode={selectingMultipleCards ? "multi" : undefined}
              selectedIdeas={selectedIdeas}
              setSelectedIdeas={setSelectedIdeas}
              laneFilter={projectBoardLanesFilter}
              checkUpdateProjectLane={checkUpdateProjectLane}
              checkUpdateProjectLaneMulti={checkUpdateProjectLaneMulti}
            />
          : null}
          {(
            !basicIdeaQuery.isFetching &&
            data.ideas.length === 0 &&
            ideaViewType !== "board"
          ) ?
            <EmptyBox>
              <h2>{t("challenge.ideas.noneTitle")}</h2>
              {challenge.stage === "published" && canMakeIdea && (
                <Button
                  primary
                  icon="plus"
                  content={
                    challenge.buttonText ?
                      challenge.buttonText
                    : t("challenge.ideas.createYourIdea")
                  }
                  onClick={() => newIdea()}
                />
              )}
            </EmptyBox>
          : null}
          {(
            !basicIdeaQuery.isFetching &&
            data.ideas.length > 0 &&
            ideaViewType !== "board"
          ) ?
            <Pagination
              limit={limit}
              limitValues={
                ideaViewType === "list" ? [15, 30, 50, 100] : [16, 32, 52, 100]
              }
              page={page}
              total={data.total}
              updateFilters={updateFilters}
            />
          : null}
        </StyledIdeaItemsContainer>
      : null}
    </StyledIdeasContainer>
  );
};

export default Ideas;
